/**
 * Customer authentication API client (Phase B · Feb 2026)
 * =======================================================
 *
 * A single centralized wrapper around fetch() for every customer-side
 * auth call. Handles:
 *
 *   - `credentials: "include"` so the HttpOnly session cookie is sent.
 *   - CSRF token pinning from the `__Host-gf_csrf` (prod) / `gf_csrf`
 *     (dev) cookie, sent as `X-CSRF-Token` on all mutations.
 *   - CSRF rotation post-login / post-activation / post-password-change
 *     (the response body's `csrf_token` value overrides the cached one
 *     until the browser reads the new cookie).
 *   - Uniform error surface — the API detail string is bubbled up as
 *     an Error with a `.status` field so callers just `try/catch`.
 *
 * Explicit non-goals
 * ------------------
 *   - Never touches localStorage.
 *   - Never stores the raw session token in JS. The HttpOnly cookie
 *     is the only source of truth for authentication.
 *   - No Bearer token handling on this path — Bearer stays for API
 *     clients / test scripts, not the browser.
 */

const BASE = process.env.REACT_APP_BACKEND_URL;

/** CSRF token cache — refreshed from cookie AND from login/change responses. */
let _csrf = null;

function _readCsrfCookie() {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const raw of cookies) {
    const [k, v] = raw.trim().split("=");
    if (k === "__Host-gf_csrf" || k === "gf_csrf") return decodeURIComponent(v || "");
  }
  return null;
}

export function getCsrf() {
  return _csrf || _readCsrfCookie();
}

export function setCsrf(v) { _csrf = v || null; }

async function _do(method, path, body, { skipCsrf = false } = {}) {
  const url = `${BASE}${path}`;
  const headers = { "Content-Type": "application/json" };
  const isMutation = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  if (isMutation && !skipCsrf) {
    const token = getCsrf();
    if (token) headers["X-CSRF-Token"] = token;
  }
  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const detail = _formatDetail(data && data.detail);
    const err = new Error(detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  // Rotate cached CSRF if the endpoint returned one.
  if (data && data.csrf_token) setCsrf(data.csrf_token);
  return data;
}

function _formatDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : String(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Endpoints ------------------------------------------------------------

export const customerAuth = {
  login:          (email, password) => _do("POST", "/api/customer-auth/login",           { email, password }, { skipCsrf: true }),
  logout:         ()                => _do("POST", "/api/customer-auth/logout"),
  status:         ()                => _do("GET",  "/api/customer-auth/status"),
  csrf:           ()                => _do("GET",  "/api/customer-auth/csrf"),
  forgot:         (email)           => _do("POST", "/api/customer-auth/forgot-password", { email }, { skipCsrf: true }),
  reset:          (token, password) => _do("POST", "/api/customer-auth/reset-password",  { token, password }, { skipCsrf: true }),
  changePassword: ({ current_password, new_password }) =>
                                       _do("POST", "/api/customer-auth/change-password", { current_password, new_password }),
  activatePassword: (token, password) =>
                                       _do("POST", "/api/portal/activate",               { token, method: "password", password }, { skipCsrf: true }),
};

// Refresh the cached CSRF from the cookie on every hard load.
export async function primeCsrfFromCookie() {
  const v = _readCsrfCookie();
  if (v) setCsrf(v);
  return v;
}

// Auto-prime when the module loads in a browser context.
if (typeof window !== "undefined") { primeCsrfFromCookie(); }
